import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import type { Player } from "@/types/api.types";

interface PlayerViewProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  player: Player | null;
}

const positionColors: Record<string, string> = {
  GK: "bg-yellow-100 text-yellow-800",
  DEF: "bg-blue-100 text-blue-800",
  MID: "bg-green-100 text-green-800",
  FWD: "bg-red-100 text-red-800",
};

export default function PlayerView({
  open,
  onOpenChange,
  player,
}: PlayerViewProps) {
  if (!player) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-white">
        <DialogHeader>
          <DialogTitle>Player Details</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Photo & Name */}
          <div className="flex items-center gap-4">
            {player.photo ? (
              <img
                src={player.photo}
                alt={player.name}
                className="w-20 h-20 rounded-full object-cover border-2"
              />
            ) : (
              <div className="w-20 h-20 rounded-full bg-gray-200 flex items-center justify-center text-2xl font-bold text-gray-500">
                {player.name.charAt(0)}
              </div>
            )}
            <div>
              <h3 className="text-2xl font-bold">{player.name}</h3>
              <div className="flex items-center gap-2 mt-1">
                <Badge className={positionColors[player.position] || ""}>
                  {player.position}
                </Badge>
                {player.jerseyNumber && (
                  <Badge variant="outline">#{player.jerseyNumber}</Badge>
                )}
                {player.isActive ? (
                  <Badge className="bg-green-600 text-white">Active</Badge>
                ) : (
                  <Badge variant="destructive" className="bg-red-600">
                    Inactive
                  </Badge>
                )}
              </div>
            </div>
          </div>

          {/* Details Grid */}
          <div className="grid grid-cols-2 gap-4 border-t pt-4">
            <div>
              <p className="text-sm font-medium text-gray-500">Nation</p>
              <div className="flex items-center gap-2 mt-1">
                {player.nation?.flagUrl && (
                  <img
                    src={player.nation.flagUrl}
                    alt={player.nation.name}
                    className="w-8 h-5 object-cover rounded"
                  />
                )}
                <span className="font-medium">
                  {player.nation?.name || "—"}
                </span>
                {player.nation?.code && (
                  <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">
                    {player.nation.code}
                  </code>
                )}
              </div>
            </div>

            <div>
              <p className="text-sm font-medium text-gray-500">Club</p>
              <p className="font-medium mt-1 flex items-center gap-2">
                {player.club?.logoUrl && (
                  <img src={player.club.logoUrl} alt="" className="w-5 h-5 rounded-full object-cover" />
                )}
                {player.club?.name || "—"}
              </p>
            </div>

            <div>
              <p className="text-sm font-medium text-gray-500">Age</p>
              <p className="font-medium mt-1">{player.age ?? "—"}</p>
            </div>

            <div>
              <p className="text-sm font-medium text-gray-500">Total Points</p>
              <p className="text-3xl font-bold text-blue-600 mt-1">
                {player.points ?? 0}
              </p>
            </div>

            <div>
              <p className="text-sm font-medium text-gray-500">Jersey Number</p>
              <p className="text-2xl font-bold mt-1">
                {player.jerseyNumber ? `#${player.jerseyNumber}` : "—"}
              </p>
            </div>

            <div>
              <p className="text-sm font-medium text-gray-500">Created</p>
              <p className="text-sm mt-1">
                {new Date(player.createdAt).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </p>
            </div>

            <div>
              <p className="text-sm font-medium text-gray-500">Last Updated</p>
              <p className="text-sm mt-1">
                {new Date(player.updatedAt).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </p>
            </div>
          </div>

          {/* Eliminated nation warning */}
          {player.nation?.isEliminated && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-800">
                <strong>⚠️ Nation Eliminated:</strong> {player.nation.name} has
                been eliminated from the tournament. This player is deactivated.
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
